export type EmptyStateVariant = 'empty' | 'filtered' | 'error' | 'permission';

export type EmptyStateProps = {
  title: string;
  body?: string;
  variant?: EmptyStateVariant;
  cta?: { label: string; onClick: () => void };
  errorCode?: string; // only rendered when variant === 'error'
};

const ICON: Record<EmptyStateVariant, React.ReactElement> = {
  empty: <Circle />,
  filtered: <Funnel />,
  error: <Bang />,
  permission: <Lock />,
};

/**
 * Single-frame "nothing here" surface. The four documented variants cover
 * the common reasons a list / detail surface has no rows to show; each
 * variant gets its own glyph + tone but the layout is identical.
 */
export function EmptyState({
  title,
  body,
  variant = 'empty',
  cta,
  errorCode,
}: EmptyStateProps) {
  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className="mx-auto flex max-w-md flex-col items-center gap-sp-3 rounded-r-card border border-gray-300 bg-gray-50 p-sp-9 text-center"
      data-variant={variant}
    >
      <span aria-hidden className="text-gray-700">
        {ICON[variant]}
      </span>
      <h2 className="text-fs-statement font-medium text-gray-900">{title}</h2>
      {body ? <p className="text-fs-body text-gray-700">{body}</p> : null}
      {variant === 'error' && errorCode ? (
        <code className="rounded-r-chip bg-gray-100 px-sp-2 py-sp-1 font-mono text-fs-meta text-gray-700">
          {errorCode}
        </code>
      ) : null}
      {cta ? (
        <button
          type="button"
          onClick={cta.onClick}
          className="mt-sp-2 rounded-r-default border border-gray-900 px-sp-4 py-sp-1 text-fs-body font-medium text-gray-900 hover:bg-gray-100"
        >
          {cta.label}
        </button>
      ) : null}
    </div>
  );
}

function Circle() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden>
      <circle cx="14" cy="14" r="11" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function Funnel() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden>
      <path d="M5 6h18l-7 9v6l-4 2v-8L5 6z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function Bang() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden>
      <path d="M14 4l11 19H3L14 4z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M14 12v5M14 19v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function Lock() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden>
      <rect x="6" y="13" width="16" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 13v-3a4 4 0 1 1 8 0v3" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
